import type { StockTransfertDetail } from './logiciel-caisse-stock-transfert.service';
import { stockTransfertIsReceptionLabel } from './logiciel-caisse-stock-transfert-map.util';

export type LcDetailModalDocumentOptions = {
  documentTypeLabel?: string;
  departLabel?: string;
  receptionLabel?: string;
  /** Nom établissement / station (émetteur & récepteur). */
  etablissementLabel?: string;
  /** Bon de réception : quantité affichée = reçu LC (pas expédié). */
  afficherQuantiteRecue?: boolean;
};

const IS_RECEPTION_TERMINEE = 1;

/**
 * Après correction PV (approuver transfert) : pour un écart manquant (reçu < envoyé),
 * la quantité affichée = envoyée − |écart| (= reçue) ; reste en circulation → 0.
 */
export function applyLcTransfertQtyAfterPvEcartCorrection(
  qteEnvoyee: number,
  qteRecue: number,
  options: {
    correctionApplied: boolean;
    field: 'quantite' | 'reste_circulation';
  }
): number {
  const env = Math.max(0, Math.trunc(qteEnvoyee));
  const rec = Math.max(0, Math.trunc(qteRecue));
  const ecart = rec - env;
  if (options.field === 'reste_circulation') {
    const reste = Math.max(0, env - rec);
    if (options.correctionApplied && ecart < 0) {
      return Math.max(0, reste + ecart);
    }
    return reste;
  }
  if (options.correctionApplied && ecart < 0) {
    return Math.max(0, env + ecart);
  }
  return env;
}

/** Qtés envoyée / reçue alignées sur le détail API LC (barcodes + is_reception entête). */
export function readLcTransfertBarcodeQuantites(
  bc: {
    quantite?: number;
    quantite_envoyee?: number;
    quantite_recue?: number;
    is_reception?: number;
  },
  headerIsReception: number
): { qteEnvoyee: number; qteRecue: number } {
  let qteEnvoyee = 0;
  if (bc.quantite_envoyee != null && String(bc.quantite_envoyee).trim() !== '') {
    qteEnvoyee = Math.trunc(Number(bc.quantite_envoyee) || 0);
  }
  if (qteEnvoyee === 0 && Number(headerIsReception) !== IS_RECEPTION_TERMINEE) {
    const lineIs = Number(bc.is_reception);
    const q = Math.trunc(Number(bc.quantite) || 0);
    qteEnvoyee = lineIs === IS_RECEPTION_TERMINEE ? 0 : q;
  }

  let qteRecue = 0;
  if (bc.quantite_recue != null && String(bc.quantite_recue).trim() !== '') {
    qteRecue = Math.trunc(Number(bc.quantite_recue) || 0);
  }
  if (qteRecue === 0 && Number(headerIsReception) === IS_RECEPTION_TERMINEE) {
    const lineIs = Number(bc.is_reception);
    const q = Math.trunc(Number(bc.quantite) || 0);
    qteRecue = lineIs === IS_RECEPTION_TERMINEE || q > 0 ? q : 0;
  }

  // Transferts déjà clos avant la scission envoyé / réceptionné en base
  if (
    qteRecue === 0 &&
    qteEnvoyee > 0 &&
    Number(headerIsReception) === IS_RECEPTION_TERMINEE
  ) {
    qteRecue = qteEnvoyee;
  }
  if (
    qteEnvoyee === 0 &&
    qteRecue > 0 &&
    Number(headerIsReception) === IS_RECEPTION_TERMINEE
  ) {
    qteEnvoyee = qteRecue;
  }

  if (Number(headerIsReception) === IS_RECEPTION_TERMINEE && qteRecue === 0) {
    const qHeader = Math.trunc(Number(bc.quantite) || 0);
    if (qHeader > 0) {
      qteRecue = qHeader;
    }
  }

  return { qteEnvoyee, qteRecue };
}

function readLcTransfertLineDescription(
  bc: { description?: string | null; note?: string | null },
  lg?: { description?: string | null; note?: string | null }
): string | undefined {
  const fromBcNote = bc?.note != null ? String(bc.note).trim() : '';
  if (fromBcNote) {
    return fromBcNote;
  }
  const fromBc = bc?.description != null ? String(bc.description).trim() : '';
  if (fromBc) {
    return fromBc;
  }
  const fromLgNote = lg?.note != null ? String(lg.note).trim() : '';
  if (fromLgNote) {
    return fromLgNote;
  }
  const fromLg = lg?.description != null ? String(lg.description).trim() : '';
  return fromLg || undefined;
}

export function lcTransfertVariantKey(
  productId: number | null | undefined,
  codeBarre: string | null | undefined,
  couleur: string | null | undefined,
  taille: string | null | undefined
): string {
  const bc = codeBarre != null ? String(codeBarre).trim() : '';
  const c = couleur != null ? String(couleur).trim().toLowerCase() : '';
  const t = taille != null ? String(taille).trim().toLowerCase() : '';
  if (bc && bc !== '(sans code)') {
    return `${bc}\0${c}\0${t}`;
  }
  const pid = productId != null ? Number(productId) : 0;
  return `p${pid}\0${c}\0${t}`;
}

function mergeLcReceptionVariantes(detail: StockTransfertDetail): Map<
  string,
  { quantite_envoyee: number; quantite_recue: number }
> {
  const map = new Map<string, { quantite_envoyee: number; quantite_recue: number }>();
  const headerIsReception = Number(detail.is_reception);

  for (const lg of detail.lignes ?? []) {
    for (const bc of lg.barcodes ?? []) {
      const { qteEnvoyee, qteRecue } = readLcTransfertBarcodeQuantites(bc, headerIsReception);
      const key = lcTransfertVariantKey(
        bc.product_id ?? null,
        bc.code_barre,
        bc.couleur,
        bc.taille
      );
      const prev = map.get(key);
      map.set(key, {
        quantite_envoyee: Math.max(prev?.quantite_envoyee ?? 0, qteEnvoyee),
        quantite_recue: Math.max(prev?.quantite_recue ?? 0, qteRecue),
      });
    }
  }

  for (const v of detail.reception_etat?.variantes ?? []) {
    const key = lcTransfertVariantKey(v.product_id, v.code_barre, v.couleur, v.taille);
    const prev = map.get(key);
    map.set(key, {
      quantite_envoyee: Math.max(
        prev?.quantite_envoyee ?? 0,
        Math.max(0, Math.trunc(Number(v.quantite_envoyee) || 0))
      ),
      quantite_recue: Math.max(
        prev?.quantite_recue ?? 0,
        Math.max(0, Math.trunc(Number(v.quantite_recue) || 0))
      ),
    });
  }

  return map;
}

export type LcBonReceptionLigneQuantiteMode = 'recue' | 'sortie';

/** Bon de réception ERP : qtés LC (reçu ou expédié / sortie) sur les lignes document. */
export function applyLcReceivedQuantitiesToDocumentLignes(
  lignes: Record<string, unknown>[],
  detail: StockTransfertDetail,
  options: { quantiteMode?: LcBonReceptionLigneQuantiteMode } = {}
): Record<string, unknown>[] {
  const quantiteMode = options.quantiteMode ?? 'recue';
  const byVariant = mergeLcReceptionVariantes(detail);
  if (!byVariant.size) {
    return lignes;
  }

  return lignes.map((ligne) => {
    const codeBar = (ligne['code_bar'] ?? ligne['code_barre']) as string | null;
    const key = lcTransfertVariantKey(
      ligne['product_id'] as number | null,
      codeBar,
      ligne['couleur'] as string | null,
      ligne['taille'] as string | null
    );
    let state = byVariant.get(key);
    if (!state) {
      const bcOnly = codeBar != null ? String(codeBar).trim() : '';
      if (bcOnly) {
        for (const [k, v] of byVariant) {
          if (k.startsWith(`${bcOnly}\0`)) {
            state = v;
            break;
          }
        }
      }
    }
    if (!state) {
      return ligne;
    }
    const qRec = Math.max(0, Math.trunc(state.quantite_recue));
    const qSortie = Math.max(0, Math.trunc(state.quantite_envoyee));
    const qDisplay = quantiteMode === 'sortie' ? qSortie : qRec;
    const pu =
      parseFloat(String(ligne['prix_ht'] ?? ligne['prix_unitaire'] ?? 0).replace(',', '.')) || 0;
    return {
      ...ligne,
      qnt: qDisplay,
      quantite: qDisplay,
      qte_recue: qRec,
      qte_envoyee: qSortie,
      total_ht: pu * qDisplay,
    };
  });
}

/**
 * Snapshot document ERP pour `DetailInventaireComponent` (même popup que bon transfert / inventaire).
 */
export function mapLcStockTransfertDetailToDetailModalDocument(
  detail: StockTransfertDetail,
  options: LcDetailModalDocumentOptions = {}
): Record<string, unknown> {
  const headerIsReception = Number(detail.is_reception);
  const correctionApplied = detail.pv_ecart_stock_corrected === true;
  const lignes_document: Record<string, unknown>[] = [];
  for (const lg of detail.lignes || []) {
    const ref = String(lg.reference ?? '').trim();
    const lib = String(lg.libelle_article ?? '').trim();
    for (const bc of lg.barcodes || []) {
      const hasMergedQty =
        bc.quantite_envoyee != null && bc.quantite_recue != null;
      const { qteEnvoyee, qteRecue } = hasMergedQty
        ? {
            qteEnvoyee: Math.trunc(Number(bc.quantite_envoyee) || 0),
            qteRecue: Math.trunc(Number(bc.quantite_recue) || 0),
          }
        : readLcTransfertBarcodeQuantites(bc, headerIsReception);
      const ecart = qteRecue - qteEnvoyee;
      let reste = Math.max(0, qteEnvoyee - qteRecue);
      let qntDisplay = options.afficherQuantiteRecue
        ? qteRecue
        : headerIsReception === IS_RECEPTION_TERMINEE
          ? Math.max(qteEnvoyee, qteRecue)
          : qteEnvoyee;
      if (correctionApplied && ecart < 0) {
        reste = applyLcTransfertQtyAfterPvEcartCorrection(qteEnvoyee, qteRecue, {
          correctionApplied: true,
          field: 'reste_circulation',
        });
        if (!options.afficherQuantiteRecue) {
          qntDisplay = applyLcTransfertQtyAfterPvEcartCorrection(qteEnvoyee, qteRecue, {
            correctionApplied: true,
            field: 'quantite',
          });
        }
      }
      const descriptionModal = readLcTransfertLineDescription(bc, lg);
      lignes_document.push({
        product_id: bc.product_id,
        qnt: qntDisplay,
        quantite: qntDisplay,
        qte_envoyee: qteEnvoyee,
        qte_recue: qteRecue,
        reste_circulation: reste,
        ecart,
        code_bar: bc.code_barre,
        code_barre: bc.code_barre,
        reference: ref || null,
        ref_interne: ref || null,
        ref_fabricant: ref || null,
        ref_fabriquant: ref || null,
        libelle: lib || 'Article',
        designation: lib || 'Article',
        couleur: bc.couleur ?? null,
        taille: bc.taille ?? null,
        unite: 'U',
        tva: 0,
        prix_ht: 0,
        prix_unitaire: 0,
        remise: 0,
        ...(descriptionModal ? { description: descriptionModal } : {}),
      });
    }
  }

  const typeLabel = options.documentTypeLabel?.trim() || 'Bon réception';
  const etablissement = options.etablissementLabel?.trim() || '—';
  const entrepotDepart =
    options.departLabel?.trim() ||
    (detail.entrepot_id_depart ? `Entrepôt #${detail.entrepot_id_depart}` : '—');
  const entrepotReception =
    options.receptionLabel?.trim() ||
    (detail.entrepot_id_reception ? `Entrepôt #${detail.entrepot_id_reception}` : '—');

  return {
    id: detail.id,
    ref_externe: detail.num_document,
    reference: detail.num_document,
    date: detail.date_transfert,
    date_document: detail.date_transfert,
    note: detail.note,
    type_document_id: 16,
    typeDocument: { titre: typeLabel },
    type_document: { titre: typeLabel },
    statut: { titre: stockTransfertIsReceptionLabel(detail.is_reception) },
    total_ht: 0,
    total_tva: 0,
    total_ttc: 0,
    net_payer: 0,
    timbre_fiscal: 0,
    entrepot_id: detail.entrepot_id_depart,
    entrepot_dest_id: detail.entrepot_id_reception,
    entrepot: options.departLabel
      ? { nom: options.departLabel, libelle: options.departLabel }
      : { id: detail.entrepot_id_depart },
    entrepot_destination: options.receptionLabel
      ? { nom: options.receptionLabel, libelle: options.receptionLabel }
      : { id: detail.entrepot_id_reception },
    lignes_document,
    lignesDocument: lignes_document,
    _lc_stock_transfert_detail: true,
    _lc_bon_reception_detail: true,
    _lc_is_reception: headerIsReception,
    _lc_bon_reception_cree: detail.bon_reception_cree === true,
    _lc_circulation_cree: detail.circulation_cree === true,
    _lc_emetteur: {
      etablissement,
      entrepot: entrepotDepart,
    },
    _lc_recepteur: {
      etablissement,
      entrepot: entrepotReception,
    },
    _lc_pv_ecart_stock_corrected: correctionApplied,
  };
}

/**
 * Lignes approve-vente pour bon transfert issu d’un PV d’écart : aligné sur le détail LC
 * (quantités après correction stock / transfert entrepôt), pas les seules lignes d’écart ERP.
 */
export function buildApproveDocumentLignesFromPvEcartLcTransfertDetail(
  detail: StockTransfertDetail
): Record<string, unknown>[] {
  const headerIsReception = Number(detail.is_reception) || 0;
  const lignes_document: Record<string, unknown>[] = [];
  for (const lg of detail.lignes || []) {
    const ref = String(lg.reference ?? '').trim() || '-';
    const lib = String(lg.libelle_article ?? '').trim();
    for (const bc of lg.barcodes || []) {
      const hasMergedQty =
        bc.quantite_envoyee != null && bc.quantite_recue != null;
      const { qteEnvoyee, qteRecue } = hasMergedQty
        ? {
            qteEnvoyee: Math.trunc(Number(bc.quantite_envoyee) || 0),
            qteRecue: Math.trunc(Number(bc.quantite_recue) || 0),
          }
        : readLcTransfertBarcodeQuantites(bc, headerIsReception);
      const qSigned = Math.trunc(Number(bc.quantite) || 0);
      if (qteEnvoyee === 0 && qteRecue === 0 && qSigned === 0) {
        continue;
      }
      const ecartSigned = qteRecue - qteEnvoyee;
      const lineQty = qSigned !== 0 ? qSigned : ecartSigned;
      const descriptionApprove = readLcTransfertLineDescription(bc, lg);
      lignes_document.push({
        product_id: bc.product_id,
        qnt: lineQty,
        quantite: lineQty,
        qte_envoyee: qteEnvoyee,
        qte_recue: qteRecue,
        ecart: ecartSigned,
        code_bar: bc.code_barre,
        code_barre: bc.code_barre,
        reference: ref === '-' ? null : ref,
        ref_interne: ref === '-' ? null : ref,
        libelle: lib || 'Article',
        designation: lib || 'Article',
        couleur: bc.couleur ?? null,
        taille: bc.taille ?? null,
        unite: 'U',
        tva: 0,
        prix_ht: 0,
        prix_unitaire: 0,
        remise: 0,
        total_ht: 0,
        ...(descriptionApprove ? { description: descriptionApprove } : {}),
      });
    }
  }
  return lignes_document;
}

/** Document pour `ApproveVenteComponent` (bon de transfert depuis transfert entrepôt LC). */
export function mapLcStockTransfertDetailToApproveDocument(
  detail: StockTransfertDetail,
  options: LcDetailModalDocumentOptions = {}
): Record<string, unknown> {
  const snapshot = mapLcStockTransfertDetailToDetailModalDocument(detail, {
    ...options,
    documentTypeLabel: options.documentTypeLabel?.trim() || 'Bon de transfert',
  });
  return {
    ...snapshot,
    type_document_id: 16,
    typeDocument: { titre: 'BON TRANSFERT' },
    type_document: { titre: 'BON TRANSFERT' },
    parametrage_doc: {
      color: '#134AD3',
      label: 'Bon de transfert',
      aff_code_bar: true,
      quantite: true,
      reference: false,
      aff_montant_total: true,
    },
    hidden_client_fields: {
      logiciel_caisse_transfert_id: detail.id,
      stock_transfert_id: detail.id,
      nomClient: false,
      identifiant: false,
      numeroIdentification: false,
      adresse: false,
      email: false,
    },
    hidden_fournisseur_fields: {
      logiciel_caisse_entrepot_id: detail.entrepot_id_depart,
      logiciel_caisse_entrepot_dest_id: detail.entrepot_id_reception,
      logiciel_caisse_transfert_id: detail.id,
      nomEntreprise: false,
      numeroIdentification: false,
      responsable: false,
      telephone: false,
      adresse: false,
    },
    _lc_bon_reception_detail: false,
    _lc_stock_transfert_approve: true,
    _lc_bon_reception_cree: detail.bon_reception_cree === true,
    _lc_circulation_cree: detail.circulation_cree === true,
  };
}
